import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Clock, Facebook, Twitter, Linkedin, Instagram, Youtube } from 'lucide-react';
import { getSiteSettings } from '../utils/api';

const Footer = () => {
  const [settings, setSettings] = useState({
    office_address: '',
    office_hours: '',
    office_phone: '',
    office_email: '',
    facebook_url: '',
    instagram_url: '',
    tiktok_url: '',
    twitter_url: '',
    linkedin_url: '',
    youtube_url: ''
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await getSiteSettings();
        if (res.data) {
          setSettings({
            office_address: res.data.office_address || '',
            office_hours: res.data.office_hours || '',
            office_phone: res.data.office_phone || '',
            office_email: res.data.office_email || '',
            facebook_url: res.data.facebook_url || '',
            instagram_url: res.data.instagram_url || '',
            tiktok_url: res.data.tiktok_url || '',
            twitter_url: res.data.twitter_url || '',
            linkedin_url: res.data.linkedin_url || '',
            youtube_url: res.data.youtube_url || ''
          });
        }
      } catch (error) {
        console.error('Failed to load site settings:', error);
      }
    };
    
    loadSettings();
  }, []);

  // Check if any social links are set
  const hasSocialLinks = settings.facebook_url || settings.instagram_url || settings.twitter_url || settings.linkedin_url || settings.youtube_url || settings.tiktok_url;

  return (
    <footer className="bg-[#8B1818] text-white mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Products & Services */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Products & Services</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/applications" className="text-white/80 hover:text-white transition-colors">Applications</Link></li>
              <li><Link to="/products/ebooks" className="text-white/80 hover:text-white transition-colors">eBooks</Link></li>
              <li><Link to="/products/souvenirs" className="text-white/80 hover:text-white transition-colors">Souvenirs</Link></li>
              <li><Link to="/booking" className="text-white/80 hover:text-white transition-colors">Book Consultation</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Resources</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/forum" className="text-white/80 hover:text-white transition-colors">Forum</Link></li>
              <li><Link to="/faqs" className="text-white/80 hover:text-white transition-colors">FAQs</Link></li>
              <li><Link to="/newsroom" className="text-white/80 hover:text-white transition-colors">Newsroom</Link></li>
            </ul>
          </div>

          {/* About Us */}
          <div>
            <h4 className="font-semibold mb-4 text-white">About Us</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/who-we-are" className="text-white/80 hover:text-white transition-colors">Who We Are</Link></li>
              <li><Link to="/about" className="text-white/80 hover:text-white transition-colors">Our Focus</Link></li>
              <li><Link to="/logo" className="text-white/80 hover:text-white transition-colors">Our Logo</Link></li>
            </ul>
          </div>

          {/* Connect & Office Details */}
          <div>
            <h4 className="font-semibold mb-4 text-white">Connect</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/contact" className="text-white/80 hover:text-white transition-colors flex items-center">
                  <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                  Contact Us
                </Link>
              </li>
              {settings.office_email && (
                <li>
                  <a href={`mailto:${settings.office_email}`} className="text-white/80 hover:text-white transition-colors flex items-center">
                    <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                    {settings.office_email}
                  </a>
                </li>
              )}
              {settings.office_phone && (
                <li>
                  <a href={`tel:${settings.office_phone}`} className="text-white/80 hover:text-white transition-colors flex items-center">
                    <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                    {settings.office_phone}
                  </a>
                </li>
              )}
              {settings.office_address && (
                <li>
                  <span className="text-white/80 flex items-start">
                    <MapPin className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-xs leading-relaxed">{settings.office_address}</span>
                  </span>
                </li>
              )}
              {settings.office_hours && (
                <li>
                  <span className="text-white/80 flex items-start">
                    <Clock className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-xs leading-relaxed">{settings.office_hours}</span>
                  </span>
                </li>
              )}
              {!settings.office_address && !settings.office_phone && !settings.office_email && (
                <li>
                  <span className="text-white/80 flex items-center">
                    <MapPin className="h-4 w-4 mr-2" />
                    Office Details
                  </span>
                </li>
              )}
            </ul>
            
            {/* Social Media */}
            <div className="mt-4">
              <p className="text-xs text-white/60 mb-2">Follow Us</p>
              <div className="flex space-x-3">
                {settings.facebook_url ? (
                  <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" title="Facebook">
                    <Facebook className="h-5 w-5" />
                  </a>
                ) : (
                  <span className="text-white/40 cursor-not-allowed" title="Facebook (Coming Soon)">
                    <Facebook className="h-5 w-5" />
                  </span>
                )}
                {settings.instagram_url ? (
                  <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" title="Instagram">
                    <Instagram className="h-5 w-5" />
                  </a>
                ) : (
                  <span className="text-white/40 cursor-not-allowed" title="Instagram (Coming Soon)">
                    <Instagram className="h-5 w-5" />
                  </span>
                )}
                {settings.twitter_url ? (
                  <a href={settings.twitter_url} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" title="Twitter/X">
                    <Twitter className="h-5 w-5" />
                  </a>
                ) : (
                  <span className="text-white/40 cursor-not-allowed" title="Twitter (Coming Soon)">
                    <Twitter className="h-5 w-5" />
                  </span>
                )}
                {settings.linkedin_url ? (
                  <a href={settings.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" title="LinkedIn">
                    <Linkedin className="h-5 w-5" />
                  </a>
                ) : (
                  <span className="text-white/40 cursor-not-allowed" title="LinkedIn (Coming Soon)">
                    <Linkedin className="h-5 w-5" />
                  </span>
                )}
                {settings.youtube_url && (
                  <a href={settings.youtube_url} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" title="YouTube">
                    <Youtube className="h-5 w-5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Policy Links Row */}
        <div className="border-t border-white/20 pt-6 pb-6">
          <div className="flex flex-wrap justify-center items-center gap-2 text-sm">
            <Link to="/terms" className="text-white/80 hover:text-white transition-colors">Terms of Use</Link>
            <span className="text-white/40">|</span>
            <Link to="/privacy" className="text-white/80 hover:text-white transition-colors">Privacy Policy</Link>
            <span className="text-white/40">|</span>
            <Link to="/copyright" className="text-white/80 hover:text-white transition-colors">Copyright & IP Policy</Link>
          </div>
        </div>

        {/* Brand & Motto Centered */}
        <div className="border-t border-white/20 pt-8 text-center">
          <h3 className="text-3xl font-bold mb-2" style={{fontFamily: 'Playfair Display, serif'}}>
            <span className="text-[#FFD700]">Lex</span><span className="text-white">DoRight</span>
          </h3>
          <p className="text-sm text-white/80 mb-4">Legal Clarity. Done Right.</p>
          <p className="text-xs text-white/60">&copy; {new Date().getFullYear()} <span className="text-[#FDB913] font-semibold">Lex</span>DoRight. All rights reserved.</p>
        </div>

        {/* Powered By Section */}
        <div className="border-t border-white/20 mt-8 pt-6 pb-4 text-center">
          <p className="text-xs text-white/50 mb-4">Powered by:</p>
          <div className="flex justify-center">
            <a 
              href="https://sragcircle.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white/20 backdrop-blur-sm rounded-xl px-6 py-4 hover:bg-white/30 transition-colors"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_legal-hub-36/artifacts/jjh6va50_SRAGCircle-transparent-website.png" 
                alt="SRAGCircle OPC - Systems, Resources & Automations Group" 
                className="h-24 object-contain"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
